import { Toaster } from '@repo/ui/components/Toaster';
import { useEffect, useMemo, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import AppRoutes from './AppRoutes';
import RequireAuth from './components/auth/RequireAuth';
import Navigation from './components/custom/Navigation';
import { WhatsNewBanner } from './components/custom/WhatsNewBanner';
import { SkipToMain } from './components/custom/SkipToMain';
import { MenuContext } from './contexts/MenuContext';
import { UniSelectorProvider } from './contexts/uni-selector/UniSelectorContext';
import { GrowthBook, GrowthBookProvider } from '@growthbook/growthbook-react';
import { DEV_ENVIRONMENTS } from './config';
import { useEmbeddedAuth } from './contexts/embedded-auth/useEmbeddedAuth';
import { useGetUser } from './api/useUser';
import { cn } from '@repo/ui/utils';

function App() {
  const [menu, setMenu] = useState<boolean>(true);
  const location = useLocation();
  const isHome = location.pathname === '/';
  const [selectedUniversityId, setSelectedUniversityId] = useState<
    string | null
  >(null);
  const { isAuthenticated } = useEmbeddedAuth();
  const { data: user, isLoading: isLoadingUser } = useGetUser(isAuthenticated);

  // Check if user needs to accept T&Cs
  const needsToAcceptTerms =
    user && !user.metadata?.termsAndConditionsAcceptedAt && !isLoadingUser;

  const growthbook = useMemo(() => {
    const gb = new GrowthBook({
      apiHost: 'https://cdn.growthbook.io',
      clientKey: import.meta.env.VITE_GROWTHBOOK_CLIENT_KEY,
      enableDevMode: true,
      attributes: {},
    });
    gb.init({
      streaming: !DEV_ENVIRONMENTS.includes(import.meta.env.VITE_BUILD),
    });
    return gb;
  }, []);

  useEffect(() => {
    const savedUniversityId = sessionStorage.getItem('selectedUniversityId');
    if (savedUniversityId) {
      setSelectedUniversityId(savedUniversityId);
    }
  }, []);

  useEffect(() => {
    sessionStorage.setItem('selectedUniversityId', selectedUniversityId || '');
  }, [selectedUniversityId]);

  return (
    <GrowthBookProvider growthbook={growthbook}>
      <RequireAuth>
        <UniSelectorProvider
          value={{ selectedUniversityId, setSelectedUniversityId }}
        >
          <MenuContext.Provider value={{ menu, setMenu }}>
            <SkipToMain />
            <Navigation />
            <div className={`app-container ${isHome ? 'homepage' : ''}`}>
              <div
                id="app"
                className={cn(
                  'sidebar-padding',
                  !menu ? 'collapsed' : '',
                  needsToAcceptTerms && 'blur-sm pointer-events-none'
                )}
              >
                <WhatsNewBanner />
                <AppRoutes />
                <Outlet />
                <Toaster />
              </div>
            </div>
          </MenuContext.Provider>
        </UniSelectorProvider>
      </RequireAuth>
    </GrowthBookProvider>
  );
}

export default App;
